/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import {
  LayoutDashboard,
  FileStack,
  BarChart3,
  FileBarChart2,
  Settings,
  Plus,
  LogOut,
  Menu,
  Shield,
  HelpCircle,
  ChevronDown,
  Filter,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { TorqeLogo } from "@/components/TorqeLogo";
import { NotificationBell } from "./NotificationBell";
import { Breadcrumbs } from "@/components/ui/breadcrumbs";
import { useBreadcrumbs, useShouldShowBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useState } from "react";
import { useFilter } from "@/context/FilterContext";
import { useBrandingValues } from "@/context/BrandingContext";
import { useCurrentUser } from "@/api/hooks/useUsers";
import {
  useUser as useAuthStoreUser,
  useAuthStore,
  useSidebarCollapsed,
  useUIStore,
} from "@/stores";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useSessionExpiry } from "@/hooks/useSessionExpiry";
import { SessionExpiryModal } from "@/components/SessionExpiryModal";
import { usePermissions } from "@/context/PermissionContext";
import { useHasExternalIntegration } from "@/api/hooks/useIntegrations";

export default function Layout() {
  const sessionExpiry = useSessionExpiry();
  const navigate = useNavigate();
  const locationObj = useLocation();
  const location = locationObj.pathname;
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const sidebarCollapsed = useSidebarCollapsed();
  const toggleSidebarCollapsed = useUIStore((s) => s.toggleSidebarCollapsed);
  const logoutFromMsal = useAuthStore((s) => s.logoutFromMsal);
  const { data: currentUser, isPending: isCurrentUserLoading } = useCurrentUser();
  const authStoreUser = useAuthStoreUser(); // Fallback while query loads
  const { environmentFilter, setEnvironmentFilter } = useFilter();
  const branding = useBrandingValues();
  const breadcrumbs = useBreadcrumbs();
  const showBreadcrumbs = useShouldShowBreadcrumbs();
  // Unique ID for mobile navigation
  const mobileNavId = "mobile-navigation";

  const handleLogout = async () => {
    await logoutFromMsal();
  };

  // Get user initials for avatar
  const getInitials = (name?: string) => {
    /* v8 ignore next -- unreachable: displayName always falls back to the "User" literal below before being passed here, so name is never falsy */
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };
  // Use currentUser from API, fallback to Zustand store while loading
  const displayName =
    currentUser?.displayName ||
    currentUser?.fullName ||
    authStoreUser?.displayName ||
    authStoreUser?.fullName ||
    "User";
  const userRole = isCurrentUserLoading
    ? ""
    : currentUser?.primaryRole?.displayName ||
      authStoreUser?.primaryRole?.displayName ||
      currentUser?.role ||
      authStoreUser?.primaryRole?.name ||
      "";

  const { can, canAny } = usePermissions();
  const { hasIntegration } = useHasExternalIntegration();

  const navItems = [
    { label: "Dashboard", icon: LayoutDashboard, href: "/", permission: "dashboard:view" },
    { label: "Submissions", icon: FileStack, href: "/submissions", permission: "submissions:view" },
    { label: "Incidents", icon: AlertTriangle, href: "/incidents", permission: "incidents:view" },
    { label: "Analytics", icon: BarChart3, href: "/analytics", permission: "analytics:view" },
    { label: "Reports", icon: FileBarChart2, href: "/reports", permission: "reports:view" },
    // Activity is unconditional: every authenticated user sees their own
    // events here. Admins with ``admin:view_audit`` see an "all users"
    // scope toggle on the page itself; non-admins are silently pinned
    // to their own user_id by the backend at ``GET /audit/me``.
    { label: "Activity", icon: Clock, href: "/activity" },
    {
      label: "Admin",
      icon: Settings,
      href: "/admin",
      permissions: [
        "admin:view_reference",
        "admin:manage_users",
        "admin:edit_config",
        "admin:manage_integrations",
      ],
    },
  ] as const;

  type NavItem = {
    label: string;
    icon: typeof LayoutDashboard;
    href: string;
    permission?: string;
    permissions?: readonly string[];
  };

  const visibleNavItems = (navItems as unknown as NavItem[]).filter((item) => {
    if (item.href === "/incidents" && !hasIntegration) return false;
    if (item.permissions) return canAny(...item.permissions);
    if (item.permission) return can(item.permission);
    return true;
  });

  const SidebarContent = ({ collapsed }: { collapsed: boolean }) => (
    <TooltipProvider delayDuration={0}>
      <div className="bg-sidebar text-sidebar-foreground border-sidebar-border flex h-full flex-col border-r transition-[width] duration-200">
        {/* Header: logo only (no hamburger – toggle is in main header) */}
        <div
          className={`border-sidebar-border flex shrink-0 items-center border-b text-primary ${collapsed ? "justify-center px-0 py-4" : "justify-start gap-3 px-4 py-4"}`}
        >
          {/* An uploaded logo is used when there is one; TorqeLogo falls back to the built-in
              mark otherwise, including when the URL is broken. logoIcon first because this slot is
              square and small -- logoLight is usually a wordmark and would squash. */}
          <TorqeLogo
            size={collapsed ? 32 : 36}
            color="currentColor"
            src={branding.logoIcon || branding.logoLight}
            alt={branding.appName}
          />
          {!collapsed && (
            <div className="flex min-w-0 flex-col">
              <span className="font-heading text-lg font-bold tracking-tight">
                {branding.appName}
              </span>
              <span className="text-[10px] leading-tight text-muted-foreground">
                {branding.appTagline}
              </span>
            </div>
          )}
        </div>

        {/* Single nav landmark — avoids duplicate unnamed <nav> inside <nav> (axe landmarks). */}
        <nav
          aria-label="Main navigation"
          className={`flex-1 space-y-1 py-4 ${collapsed ? "px-2" : "px-4"}`}
        >
          {visibleNavItems.map((item) => {
            const isActive =
              location === item.href || (item.href !== "/" && location.startsWith(item.href));
            const navButton = (
              <Button
                key={item.href}
                asChild
                variant={isActive ? "secondary" : "ghost"}
                className={`h-10 w-full gap-3 ${collapsed ? "justify-center px-0" : "justify-start"} ${isActive ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}
              >
                <Link
                  to={item.href}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={collapsed ? item.label : undefined}
                >
                  <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              </Button>
            );
            if (collapsed) {
              return (
                <Tooltip key={item.href}>
                  <TooltipTrigger asChild>{navButton}</TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              );
            }
            return navButton;
          })}
        </nav>

        {/* Help & Support */}
        <div className={`border-sidebar-border border-t py-2 ${collapsed ? "px-2" : "px-4"}`}>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 w-full" asChild>
                  <Link
                    to="/help"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Help and Documentation"
                  >
                    <HelpCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Help & Documentation</TooltipContent>
            </Tooltip>
          ) : (
            <Button variant="ghost" className="h-9 w-full justify-start px-3" asChild>
              <Link to="/help" className="gap-3 text-muted-foreground hover:text-foreground">
                <HelpCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="text-sm">Help & Documentation</span>
              </Link>
            </Button>
          )}
        </div>

        {/* User Profile: initials when collapsed, full block when expanded */}
        <div className={`border-sidebar-border border-t ${collapsed ? "p-2" : "p-4"}`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`hover:bg-sidebar-accent flex w-full cursor-pointer items-center rounded-md transition-colors ${collapsed ? "justify-center p-2" : "gap-3 p-2 text-left"}`}
                aria-label={`User menu for ${displayName}`}
                aria-haspopup="true"
              >
                <Avatar className={collapsed ? "h-9 w-9" : "h-8 w-8"}>
                  <AvatarImage
                    src={currentUser?.avatarUrl || currentUser?.avatar || authStoreUser?.avatar}
                  />
                  <AvatarFallback className="text-xs">{getInitials(displayName)}</AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <>
                    <div className="flex flex-1 flex-col text-sm">
                      <span className="font-medium">{displayName}</span>
                      {isCurrentUserLoading ? (
                        <span className="inline-block h-3 w-16 animate-pulse rounded bg-muted" />
                      ) : (
                        <span className="text-xs text-muted-foreground">{userRole}</span>
                      )}
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  void navigate("/profile");
                }}
              >
                <Shield className="mr-2 h-4 w-4" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => {
                  void handleLogout();
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Powered by Amida: Amida "A" logo when collapsed, full when expanded */}
        {branding.showPoweredBy && (
          <div className={`shrink-0 ${collapsed ? "px-2 pb-3 pt-0" : "px-4 pb-3 pt-0"}`}>
            <div className="border-sidebar-border/50 border-t pt-3">
              <div
                className={`flex rounded-lg py-[11px] ${collapsed ? "flex-col items-center gap-1" : "flex-col items-center gap-[7px]"}`}
              >
                {collapsed ? (
                  <img
                    src="/images/amida-logomark.png"
                    alt="Amida"
                    className="h-8 w-8 object-contain"
                    title="Powered by Amida"
                  />
                ) : (
                  <>
                    <div className="flex w-full items-end justify-center gap-1">
                      <span className="text-sidebar-foreground/80 text-[11px] leading-4">
                        Powered by
                      </span>
                      <img
                        src="/images/amida-wordmark.png"
                        alt="Amida"
                        className="h-[19px] w-[96px] object-contain"
                      />
                    </div>
                    <span className="text-sidebar-foreground/80 text-[11px] leading-4">
                      &copy; 2026 All Rights Reserved.
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <SessionExpiryModal
        open={sessionExpiry.showExpiryModal}
        graceSecondsRemaining={sessionExpiry.graceSecondsRemaining}
        onStayLoggedIn={sessionExpiry.onStayLoggedIn}
        onLogout={sessionExpiry.onLogout}
      />
      {/* Skip to main content link for keyboard users */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Desktop Sidebar */}
      <aside
        className={`fixed inset-y-0 z-50 hidden transition-[width] duration-200 md:block ${sidebarCollapsed ? "w-20" : "w-64"}`}
        aria-label="Desktop sidebar"
      >
        <SidebarContent collapsed={sidebarCollapsed} />
      </aside>

      {/* Mobile Sidebar - always expanded */}
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetContent
          side="left"
          className="w-64 p-0"
          id={mobileNavId}
          aria-label="Mobile navigation menu"
        >
          <SidebarContent collapsed={false} />
        </SheetContent>
      </Sheet>

      {/* Content column: top bar is a sibling of <main>, not nested inside it (clear landmark structure). */}
      <div
        className={`flex min-h-screen flex-1 flex-col transition-[margin] duration-200 ${sidebarCollapsed ? "md:ml-20" : "md:ml-64"}`}
      >
        {/* App top bar: no role="banner" — nested headers must not use banner (axe landmark-banner-is-top-level). */}
        <header
          className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-border bg-card/50 px-6 backdrop-blur-sm"
          aria-label="Application toolbar"
        >
          <div className="flex items-center gap-4">
            {/* Mobile: open sheet menu */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => {
                setIsMobileOpen(true);
              }}
              aria-label="Open mobile menu"
              aria-expanded={isMobileOpen}
              aria-controls={mobileNavId}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </Button>
            {/* Desktop: toggle sidebar collapse */}
            <Button
              variant="ghost"
              size="icon"
              className="hidden md:flex"
              onClick={toggleSidebarCollapsed}
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </Button>

            {/* Submission Type Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden h-8 items-center gap-2 md:flex"
                  aria-label={`Filter by environment: ${environmentFilter === "All" ? "All Submissions" : environmentFilter}`}
                  aria-haspopup="true"
                >
                  <Filter className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{environmentFilter === "All" ? "All Submissions" : environmentFilter}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Environment
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setEnvironmentFilter("All");
                  }}
                >
                  <div className="flex items-center gap-2">
                    {environmentFilter === "All" && (
                      <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                    )}
                    All Environments
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setEnvironmentFilter("Production");
                  }}
                >
                  <div className="flex items-center gap-2">
                    {environmentFilter === "Production" && (
                      <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    )}
                    <div className="h-2 w-2 rounded-full bg-emerald-500" />
                    Production (P)
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setEnvironmentFilter("Test");
                  }}
                >
                  <div className="flex items-center gap-2">
                    {environmentFilter === "Test" && (
                      <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    )}
                    <div className="h-2 w-2 rounded-full bg-amber-500" />
                    Test (T)
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3">
            <NotificationBell />

            {can("submissions:create") && (
              <Link to="/submissions/new">
                <Button
                  size="sm"
                  className="hidden gap-2 bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 sm:flex"
                  aria-label="Create new submission"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  New Submission
                </Button>
              </Link>
            )}
          </div>
        </header>

        {/* Breadcrumbs */}
        {showBreadcrumbs && breadcrumbs.length > 0 && (
          <div className="px-6 pb-2 pt-4 md:px-8">
            <Breadcrumbs items={breadcrumbs} showHomeIcon={true} />
          </div>
        )}

        {/* Primary page content (skip link target). */}
        <main
          id="main-content"
          tabIndex={-1}
          className={`animate-in fade-in slide-in-from-bottom-4 flex-1 p-6 duration-500 md:p-8 ${showBreadcrumbs ? "pt-2" : ""}`}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
